import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dashboard from '../../Dashboard';
import MenuContainer from '../../custom-components/menu/MenuContainer';
import AdaptiveContainer from '../../containers/AdaptiveContainer';
import {
    Box,
    Button,
    Flex,
    Input,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    useToast,
    Image,
    Switch,
    Spinner,
} from '@chakra-ui/react';
import { checkFileSize, useGetKnowladgeTh } from '../../../hooks';
import ZaraButton from '../../custom-components/buttons/Button';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { UploadFile } from '../../../graphql/mutations/UploadFile';
import { useError } from '../../../error-provider/ErrorProvider';
import { GetKnowledgeData } from '../../../graphql/queries/GetKnowledgeData';
import { DeleteKnowledgeData } from '../../../graphql/mutations/DeleteKnowledgeData';
import { UploadUrl } from '../../../graphql/mutations/UploadUrl';
import AddUrl from '../../modals/AddUrl';
import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from '../../../router/routes';
import ReadFullText from '../../modals/ReadFullText';

import successIcon from '../../../assets/icons/success.svg';
import cancelIcon from '../../../assets/icons/cancel.svg';
import { ChangeKnowledgeDataStatus } from '../../../graphql/mutations/ChangeKnowledgeDataStatus';
import { CheckProcessingStatus } from '../../../graphql/subscriptions/CheckProcessingStatus';
import { RetryUpload } from '../../../graphql/mutations/RetryUpload';

const mainStyles = {
    '@media only screen and (min-width: 465px)': {
        width: '96%',
    },

    '@media only screen and (max-width: 920px)': {
        width: '100%',
        paddingTop: '80px',
    },

    '@media only screen and (max-width: 450px)': {
        paddingTop: '0px',
        width: '345px',
    },
    '@media only screen and (min-width: 1750px)': {
        width: '1400px',
    },
};

const KnowledgeBase = () => {
    const toast = useToast();
    const router = useNavigate();
    const { showError } = useError();
    const apolloClient = useApolloClient();

    const { id } = useParams();

    const agentId = id;

    const { isOpen, onClose, onOpen } = useDisclosure();
    const { isOpen: isFullTextOpen, onClose: onFullTextClose, onOpen: onFullTextOpen } = useDisclosure();

    const fileInputRef = useRef(null);

    const [value, setValue] = useState('');
    const [fullText, setFullText] = useState('');
    const [knowledgeId, setKnowledgeId] = useState('');
    const [isOpenAddText, setIsOpenAddText] = useState(false);

    const [changeActive, { loading: activeLoading }] = useMutation(ChangeKnowledgeDataStatus, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const [deleteKnowledge, { loading: deleteLoading }] = useMutation(DeleteKnowledgeData, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const [uploadFile, { loading: fileLoading }] = useMutation(UploadFile, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const [uploadUrl, { loading: urlLoading }] = useMutation(UploadUrl, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const [retryUpload] = useMutation(RetryUpload, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const { data: knowLedgeData } = useQuery(GetKnowledgeData, {
        variables: { agentId: agentId },
        onError(error) {
            showError(undefined, error);
        },
    });

    const knowledgeArray = useMemo(() => {
        return knowLedgeData?.getKnowledgeData?.knowledgeData ?? [];
    }, [knowLedgeData]);

    const thNames = useGetKnowladgeTh();

    const [uploadedFile, setUploadedFile] = useState(false);

    const subscribeToParsing = () => {
        return apolloClient
            .watchQuery({
                variables: {
                    agentId: agentId,
                },
                query: CheckProcessingStatus,
                fetchPolicy: 'no-cache',
            })
            .subscribe({
                next(response) {
                    const uploadFiles = response.data.checkProcessingStatus;

                    const updatedKnowledge = knowledgeArray?.map((knowledge) => {
                        const matchingObj = uploadFiles?.find((file) => file.id === Number(knowledge.id));

                        if (matchingObj) {
                            return { ...knowledge, text: matchingObj.text, processingStatus: matchingObj.status };
                        }
                        return knowledge;
                    });

                    apolloClient.cache.modify({
                        id: `KnowledgeDataType:${agentId}`,
                        fields: {
                            knowledgeData() {
                                return updatedKnowledge;
                            },
                        },
                    });
                },
            });
    };

    const subscription = subscribeToParsing();

    useEffect(() => {
        return () => {
            subscription.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (uploadedFile) {
            loadFile(uploadedFile);
            setUploadedFile(null);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadedFile]);

    const onUploadImage = (e) => {
        const file = e.target.files[0];

        if (checkFileSize(file)) {
            setUploadedFile(file);
        } else {
            toast({
                title: 'Max image size is 100MB',
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };

    const deleteSpecificKnowledge = async (id) => {
        setKnowledgeId(id);
        const variables = {
            knowledgeDataId: id,
        };

        await deleteKnowledge({
            variables: variables,
            onCompleted(data) {
                if (data?.deleteKnowledgeData?.__typename === 'SuccessType') {
                    toast({
                        status: 'success',
                        isClosable: true,
                        title: data?.deleteKnowledgeData?.message,
                    });

                    apolloClient.cache.modify({
                        id: `KnowledgeDataType:${agentId}`,
                        fields: {
                            knowledgeData() {
                                return knowledgeArray.filter((knowledge) => knowledge.id !== id);
                            },
                        },
                    });
                } else {
                    toast({
                        status: 'error',
                        isClosable: true,
                        title: data?.deleteKnowledgeData?.message,
                    });
                }
            },
        });
    };

    const onRetryLoad = async (id) => {
        await retryUpload({
            variables: {
                knowledgeDataId: id,
            },
            onCompleted(data) {
                if (data?.retryUpload?.__typename === 'SuccessType') {
                    toast({
                        title: data?.retryUpload?.message,
                        status: 'success',
                        isClosable: true,
                    });
                } else {
                    toast({
                        title: data?.retryUpload?.message,
                        status: 'error',
                        isClosable: true,
                    });
                }
            },
        });
    };

    const loadFile = async (file) => {
        await uploadFile({
            variables: {
                agentId: agentId,
                file: file,
            },
            onCompleted(data) {
                if (data?.uploadFile?.__typename === 'UploadedKnowledgeDataSuccessType') {
                    toast({
                        title: data?.uploadFile?.message,
                        status: 'success',
                        isClosable: true,
                    });

                    apolloClient.cache.modify({
                        id: `KnowledgeDataType:${agentId}`,
                        fields: {
                            knowledgeData() {
                                return [...knowledgeArray, data?.uploadFile?.knowledgeData];
                            },
                        },
                    });
                } else {
                    toast({
                        title: data?.uploadFile?.message,
                        status: 'error',
                        isClosable: true,
                    });
                }
            },
        });
    };

    const loadUrl = async (url) => {
        await uploadUrl({
            variables: {
                agentId: agentId,
                url: url,
            },
            onCompleted(data) {
                if (data?.uploadUrl?.__typename === 'UploadedKnowledgeDataSuccessType') {
                    toast({
                        title: data?.uploadUrl?.message,
                        status: 'success',
                        isClosable: true,
                    });

                    apolloClient.cache.modify({
                        id: `KnowledgeDataType:${agentId}`,
                        fields: {
                            knowledgeData() {
                                return [...knowledgeArray, data?.uploadUrl?.knowledgeData];
                            },
                        },
                    });
                } else {
                    toast({
                        title: data?.uploadUrl?.message,
                        status: 'error',
                        isClosable: true,
                    });
                }
            },
        });
    };

    const onChangeStatus = async (active, knowledgeDataId) => {
        setKnowledgeId(knowledgeDataId);
        try {
            await changeActive({
                variables: {
                    knowledgeDataId: knowledgeDataId,
                    status: !active,
                },
                onCompleted(data) {
                    if (data?.changeKnowledgeDataStatus?.__typename === 'SuccessType') {
                        toast({
                            title: data?.changeKnowledgeDataStatus?.message,
                            status: 'success',
                            isClosable: true,
                        });

                        apolloClient.cache.modify({
                            id: `KnowledgeDataType:${agentId}`,
                            fields: {
                                knowledgeData() {
                                    return knowledgeArray?.map((knowledge) =>
                                        knowledge.id === knowledgeDataId ? { ...knowledge, active: !active } : knowledge
                                    );
                                },
                            },
                        });
                    } else {
                        toast({
                            title: data?.changeKnowledgeDataStatus?.message,
                            status: 'error',
                            isClosable: true,
                        });
                    }
                },
            });
        } catch (error) {
            console.log(error);
        } finally {
            setKnowledgeId('');
        }
    };

    const isDisabled = useMemo(() => {
        return fileLoading || urlLoading || deleteLoading;
    }, [fileLoading, urlLoading, deleteLoading]);

    const onOpenTextModal = (text, id) => {
        setIsOpenAddText(false);
        setFullText(text);
        setKnowledgeId(id);
        onFullTextOpen();
    };

    const onOpenAddText = () => {
        setIsOpenAddText(true);
        onFullTextOpen();
    };

    const formatedPath = (path, knowledgeId) =>
        `${path}/`.replace(':agentId/:knowledgeId/', `${agentId}/${knowledgeId}`);

    return (
        <Dashboard title='dashboard'>
            <MenuContainer>
                <AdaptiveContainer css={{ ...mainStyles }}>
                    <Box pb='20px'>
                        <Stack
                            pos='relative'
                            boxShadow=' 0px 1px 3px 0px #0000001A'
                            mt='100px'
                            ml='20px'
                            w='95%'
                            css={{
                                '@media only screen and (max-width: 450px)': {
                                    display: 'none',
                                },
                            }}
                            border='1px solid #E2E8F0'
                            borderRadius='4px'
                            p='20px'
                        >
                            <Flex gap='10px' justifyContent='space-between'>
                                <Text fontSize={{ sm: '16px', xl: '24px' }} fontWeight='700'>
                                    Agent Knowledge Base
                                </Text>
                                <Flex gap='10px'>
                                    <ZaraButton isDisabled={isDisabled} onClick={onOpenAddText}>
                                        Add Text
                                    </ZaraButton>
                                    <ZaraButton isLoading={urlLoading} isDisabled={isDisabled} onClick={onOpen}>
                                        Fetch URL
                                    </ZaraButton>
                                    <ZaraButton w='200px' isDisabled={isDisabled} cursor='pointer'>
                                        <Input
                                            pos='absolute'
                                            ref={fileInputRef}
                                            accept='.doc, .docx, .pdf, .txt, .csv, .mp4, .mp3'
                                            cursor='pointer'
                                            w='full'
                                            h='full'
                                            type='file'
                                            top='-1000px'
                                            opacity={0}
                                            onChange={onUploadImage}
                                        />
                                        <Flex
                                            onClick={handleClick}
                                            cursor='pointer'
                                            alignItems='center'
                                            justifyContent='center'
                                            borderRadius='6px'
                                            w='full'
                                            h='full'
                                            gap='5px'
                                        >
                                            <Text cursor='pointer'>Upload File</Text>
                                            {fileLoading && <Spinner />}
                                        </Flex>
                                    </ZaraButton>
                                </Flex>
                            </Flex>

                            <TableContainer
                                overflow='hidden'
                                maxH='90%'
                                w='full'
                                p='12px'
                                border='none'
                                borderRadius='12px'
                            >
                                <Table
                                    css={{
                                        th: {
                                            padding: '10px 0px',
                                            textAlign: 'center',
                                        },
                                        td: { padding: '10px 0px', textAlign: 'center' },
                                    }}
                                >
                                    <Thead>
                                        <Tr>
                                            {thNames.map((th) => (
                                                <Th
                                                    key={th.name}
                                                    color='#1A202C'
                                                    fontSize={{ md: '12px', lg: '16px' }}
                                                    fontWeight='700'
                                                    lineHeight='16px'
                                                    css={{
                                                        fontSize: 'calc(12px + 4  * (100vw / 1920))',
                                                        '@media only screen and (max-width: 1300px)': {
                                                            fontSize: {
                                                                fontSize: '11px',
                                                            },
                                                            padding: '0px',
                                                        },
                                                    }}
                                                >
                                                    {th.name}
                                                </Th>
                                            ))}
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {!knowledgeArray.length && (
                                            <Tr>
                                                <Td></Td>
                                                <Td>
                                                    <Text color='gray.400' fontWeight='700'>
                                                        Nothing found
                                                    </Text>
                                                </Td>
                                                <Td></Td>
                                                <Td></Td>
                                                <Td></Td>
                                            </Tr>
                                        )}
                                        {knowledgeArray?.map((knowledge) => {
                                            const dateTimeString = knowledge.createdAt;
                                            const dateTime = new Date(dateTimeString);
                                            const options = {
                                                month: 'long',
                                                day: 'numeric',
                                                year: 'numeric',
                                                hour: 'numeric',
                                                minute: 'numeric',
                                                second: 'numeric',
                                                hour12: true,
                                            };

                                            const americanFormat = dateTime.toLocaleString('en-US', options);

                                            return (
                                                <Tr key={knowledge.id} pos='relative'>
                                                    <Td>{knowledge.sourceType}</Td>
                                                    <Td>
                                                        <Flex justifyContent='center'>
                                                            <Text
                                                                cursor='pointer'
                                                                textDecor='underline'
                                                                maxW={{ sm: '120px', xl: '350px' }}
                                                                overflow='hidden'
                                                                textOverflow='ellipsis'
                                                                onClick={() =>
                                                                    onOpenTextModal(knowledge.text, knowledge.id)
                                                                }
                                                            >
                                                                {knowledge.text}
                                                            </Text>
                                                        </Flex>
                                                    </Td>
                                                    <Td>{americanFormat}</Td>

                                                    <Td>
                                                        <Flex justifyContent='center' alignItems='center' gap='12px'>
                                                            {knowledge.processingStatus === 'Success' && (
                                                                <>
                                                                    <Switch
                                                                        isChecked={knowledge.active}
                                                                        onChange={() =>
                                                                            onChangeStatus(
                                                                                knowledge.active,
                                                                                knowledge.id
                                                                            )
                                                                        }
                                                                    />
                                                                    <Flex
                                                                        alignItems='center'
                                                                        w='40px'
                                                                        h='24px'
                                                                        justifyContent='center'
                                                                    >
                                                                        {activeLoading &&
                                                                            knowledge.id === knowledgeId ? (
                                                                            <Spinner w='22px' h='22px' />
                                                                        ) : (
                                                                            <Image
                                                                                src={
                                                                                    knowledge.active
                                                                                        ? successIcon
                                                                                        : cancelIcon
                                                                                }
                                                                                w='24px'
                                                                                h='24px'
                                                                            />
                                                                        )}
                                                                    </Flex>
                                                                </>
                                                            )}

                                                            {knowledge.processingStatus === 'Processing' && (
                                                                <Stack alignItems='center'>
                                                                    <Text>Processing</Text>
                                                                    <Spinner w='22px' h='22px' />
                                                                </Stack>
                                                            )}

                                                            {knowledge.processingStatus === 'Failure' && (
                                                                <Stack spacing='5px'>
                                                                    <Text>Failed processing</Text>

                                                                    <Text
                                                                        _hover={{
                                                                            textDecor: 'underline',
                                                                        }}
                                                                        cursor='pointer'
                                                                        color='red'
                                                                        onClick={() => onRetryLoad(knowledge.id)}
                                                                    >
                                                                        Retry
                                                                    </Text>
                                                                </Stack>
                                                            )}
                                                        </Flex>
                                                    </Td>
                                                    <Td>
                                                        <Button
                                                            isDisabled={isDisabled}
                                                            isLoading={deleteLoading && knowledgeId === knowledge.id}
                                                            onClick={() => deleteSpecificKnowledge(knowledge.id)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Td>
                                                </Tr>
                                            );
                                        })}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                            <Flex>
                                <ZaraButton
                                    isDisabled={isDisabled}
                                    variant='secondary'
                                    onClick={() => router(PATHS.MY_AGENT)}
                                >
                                    Back
                                </ZaraButton>
                            </Flex>
                        </Stack>

                        {/*Mobile version*/}
                        <Stack
                            display='none'
                            gap='12px'
                            flexDir='column'
                            css={{
                                '@media only screen and (max-width: 450px)': {
                                    paddingTop: '70px',
                                    display: 'flex',
                                },
                            }}
                            w='100%'
                            mb='100px'
                        >
                            <Flex justifyContent='space-between'>
                                <Input
                                    w='60%'
                                    placeholder='Write your URL'
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                />

                                <ZaraButton
                                    onClick={() => loadUrl(value)}
                                    isLoading={urlLoading}
                                    isDisabled={isDisabled || !value?.trim()?.length}
                                >
                                    Fetch URL
                                </ZaraButton>
                            </Flex>
                            <ZaraButton w='full' isDisabled={isDisabled} isLoading={fileLoading} cursor='pointer'>
                                <Input
                                    pos='absolute'
                                    ref={fileInputRef}
                                    accept='.doc, .docx, .pdf, .txt, .csv, .mp4, .mp3'
                                    cursor='pointer'
                                    w='full'
                                    h='full'
                                    type='file'
                                    top='-1000px'
                                    opacity={0}
                                    onChange={onUploadImage}
                                />
                                <Flex
                                    onClick={handleClick}
                                    cursor='pointer'
                                    alignItems='center'
                                    justifyContent='center'
                                    borderRadius='6px'
                                    w='full'
                                    h='full'
                                >
                                    <Text cursor='pointer'>Upload File</Text>
                                </Flex>
                            </ZaraButton>

                            <ZaraButton w='full' isDisabled={isDisabled} onClick={() => router(PATHS.FULL_TEXT)}>
                                Add Text
                            </ZaraButton>

                            {knowledgeArray?.map((knowledge) => {
                                const dateTimeString = knowledge.createdAt;
                                const dateTime = new Date(dateTimeString);
                                const options = {
                                    month: 'long',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: 'numeric',
                                    second: 'numeric',
                                    hour12: true,
                                };
                                const americanFormat = dateTime.toLocaleString('en-US', options);

                                return (
                                    <Box
                                        bg='#F4F3FD'
                                        borderRadius='12px'
                                        key={knowledge.id}
                                        boxShadow='0px 4px 4px 0px #00000026'
                                        p='12px 15px'
                                    >
                                        <Flex pb='12px'>
                                            <Stack minW='100px' justifyContent='space-between' mr='20px'>
                                                <Text fontSize='12px' fontWeight='700' p='5px 0px'>
                                                    SOURCE TYPE
                                                </Text>
                                                <Text fontSize='12px' fontWeight='700' p='5px 0px'>
                                                    TEXT
                                                </Text>
                                                <Text fontSize='12px' fontWeight='700' p='5px 0px'>
                                                    Created date
                                                </Text>
                                                <Text fontSize='12px' fontWeight='700' p='5px 0px'>
                                                    Active
                                                </Text>
                                            </Stack>
                                            <Stack justifyContent='space-between'>
                                                <Text fontSize='14px' p='5px 0px'>
                                                    {knowledge.sourceType}
                                                </Text>
                                                <Text
                                                    textDecor='underline'
                                                    maxW='200px'
                                                    maxH='24px'
                                                    overflow='hidden'
                                                    textOverflow='ellipsis'
                                                    fontSize='14px'
                                                    p='5px 0px'
                                                    onClick={() => router(formatedPath(PATHS.FULL_TEXT, knowledge.id))}
                                                >
                                                    {knowledge.text}
                                                </Text>
                                                <Text fontSize='14px' p='5px 0px'>
                                                    {americanFormat}
                                                </Text>
                                                <Flex alignItems='center' gap='12px'>
                                                    {knowledge.processingStatus === 'Success' && (
                                                        <>
                                                            <Switch
                                                                size='lg'
                                                                isChecked={knowledge.active}
                                                                onChange={() =>
                                                                    onChangeStatus(knowledge.active, knowledge.id)
                                                                }
                                                            />
                                                            {activeLoading && knowledge.id === knowledgeId ? (
                                                                <Spinner w='22px' h='22px' />
                                                            ) : (
                                                                <Image
                                                                    src={knowledge.active ? successIcon : cancelIcon}
                                                                    w='24px'
                                                                    h='24px'
                                                                />
                                                            )}
                                                        </>
                                                    )}

                                                    {knowledge.processingStatus === 'Processing' && (
                                                        <>
                                                            <Text>Processing</Text>
                                                            <Spinner w='22px' h='22px' />
                                                        </>
                                                    )}

                                                    {knowledge.processingStatus === 'Failure' && (
                                                        <>
                                                            <Text>Failed processing</Text>

                                                            <Text
                                                                _hover={{
                                                                    textDecor: 'underline',
                                                                }}
                                                                cursor='pointer'
                                                                color='red'
                                                                onClick={() => onRetryLoad(knowledge.id)}
                                                            >
                                                                Retry
                                                            </Text>
                                                        </>
                                                    )}
                                                </Flex>
                                            </Stack>
                                        </Flex>

                                        <Button
                                            isDisabled={isDisabled}
                                            isLoading={deleteLoading && knowledgeId === knowledge.id}
                                            onClick={() => deleteSpecificKnowledge(knowledge.id)}
                                            w={{ base: 'full', sm: '50%' }}
                                            _active={{
                                                bg: 'white',
                                            }}
                                            bg='white'
                                            color='black'
                                            border='1px solid black'
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                );
                            })}
                            <Flex>
                                <ZaraButton
                                    w='full'
                                    isDisabled={isDisabled}
                                    variant='secondary'
                                    onClick={() => router(PATHS.MY_AGENT)}
                                >
                                    Back
                                </ZaraButton>
                            </Flex>
                        </Stack>
                    </Box>

                    <AddUrl isOpen={isOpen} onClose={onClose} value={value} loadUrl={loadUrl} setValue={setValue} />
                    <ReadFullText
                        agentId={agentId}
                        isOpenAddText={isOpenAddText}
                        knowledgeData={knowledgeArray}
                        knowledgeId={knowledgeId}
                        isOpen={isFullTextOpen}
                        onClose={onFullTextClose}
                        fullText={fullText}
                        setFullText={setFullText}
                    />
                </AdaptiveContainer>
            </MenuContainer>
        </Dashboard>
    );
};

export default KnowledgeBase;
